import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Clock, Calendar } from 'lucide-react';
import '../styles/form.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get dynamic urgency message based on current date
const getUrgencyMessage = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7 || 7;
  
  // Calculate end of week (Friday)
  const friday = new Date(now);
  friday.setDate(now.getDate() + daysUntilFriday);
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formattedDate = `${monthNames[friday.getMonth()]} ${friday.getDate()}`;
  
  return {
    date: formattedDate,
    isUrgent: daysUntilFriday <= 2
  };
};

const ConsultationForm = ({ onSubmit, darkMode = false, source = 'main' }) => {
  const [step, setStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [urgency] = useState(getUrgencyMessage);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    employeeCount: '',
    currentProvider: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    if (!formData.employeeCount) {
      newErrors.employeeCount = 'Employee count is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Capture partial lead after Step 1 completion
  const capturePartialLead = async () => {
    try {
      await axios.post(`${API}/partial-lead`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        source: source
      });
      // Partial lead captured successfully
    } catch (error) {
      // Don't block user flow if this fails - silent failure is acceptable here
    }
  };

  const handleStep1Continue = async (e) => {
    e.preventDefault();
    if (validateStep1()) {
      // Capture partial lead data in background
      capturePartialLead();
      // Trigger animation
      setIsAnimating(true);
      setTimeout(() => {
        setStep(2);
        setIsAnimating(false);
      }, 400);
    }
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    if (validateStep2()) {
      setIsSubmitting(true);
      try {
        // Mark partial lead as complete
        axios.put(`${API}/partial-lead/${encodeURIComponent(formData.email)}/complete`).catch(() => {});
        
        // Submit full consultation request
        const response = await axios.post(`${API}/consultation-request`, {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          company_name: formData.companyName,
          employee_count: formData.employeeCount,
          current_provider: formData.currentProvider || '',
          source: source
        });

        if (response.data.status === 'success') {
          onSubmit(formData.email);
        }
      } catch (error) {
        // Still show success to user even if email fails
        onSubmit(formData.email);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <div className={`consultation-form ${darkMode ? 'dark-mode' : ''}`}>
      {/* Urgency Indicator */}
      <div className={`form-urgency ${urgency.isUrgent ? 'urgent' : ''}`}>
        <Clock size={14} className="urgency-icon" />
        <span>
          Consultation times are <strong>filling up fast</strong> this week
        </span>
      </div>

      {/* Progress Bar */}
      <div className="form-progress">
        <div className="progress-steps">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-circle">1</div>
            <span className="step-label">Your Info</span>
          </div>
          <div className={`progress-connector ${step > 1 ? 'active' : ''}`}></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-circle">2</div>
            <span className="step-label">Company</span>
          </div>
        </div>
        <div className="progress-bar">
          <div 
            className={`progress-fill ${isAnimating ? 'animating' : ''}`} 
            style={{width: step === 1 ? '50%' : '100%'}}
          ></div>
        </div>
      </div>

      {step === 1 ? (
        <form onSubmit={handleStep1Continue} className="form-step">
          <div className="form-grid">
            <div className="form-field">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                autoComplete="given-name"
                className={errors.firstName ? 'error' : ''}
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>
            <div className="form-field">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                autoComplete="family-name"
                className={errors.lastName ? 'error' : ''}
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>
          <div className="form-field form-field-full">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              autoComplete="email"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>
          <Button type="submit" className="form-button">
            Continue
          </Button>
        </form>
      ) : (
        <form onSubmit={handleStep2Submit} className="form-step">
          <div className="form-field form-field-full">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              name="companyName"
              type="text"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              autoComplete="organization"
              className={errors.companyName ? 'error' : ''}
            />
            {errors.companyName && <span className="error-message">{errors.companyName}</span>}
          </div>
          <div className="form-grid">
            <div className="form-field">
              <Label htmlFor="employeeCount">Employee Count</Label>
              <Select
                value={formData.employeeCount}
                onValueChange={(value) => handleInputChange('employeeCount', value)}
                name="employeeCount"
              >
                <SelectTrigger className={errors.employeeCount ? 'error' : ''}>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under-10">Under 10</SelectItem>
                  <SelectItem value="11-50">11 to 50</SelectItem>
                  <SelectItem value="51-150">51 to 150</SelectItem>
                  <SelectItem value="151+">151+</SelectItem>
                </SelectContent>
              </Select>
              {errors.employeeCount && <span className="error-message">{errors.employeeCount}</span>}
            </div>
            <div className="form-field">
              <Label htmlFor="currentProvider">Current Provider (Optional)</Label>
              <Select
                value={formData.currentProvider}
                onValueChange={(value) => handleInputChange('currentProvider', value)}
                name="currentProvider"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adp">ADP TotalSource</SelectItem>
                  <SelectItem value="trinet">TriNet</SelectItem>
                  <SelectItem value="insperity">Insperity</SelectItem>
                  <SelectItem value="other">Other PEO</SelectItem>
                  <SelectItem value="none">Not with a PEO</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button type="submit" className="form-button" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Schedule Free Consultation'}
          </Button>
        </form>
      )}
    </div>
  );
};

export default ConsultationForm;